import React, { useState, useEffect } from 'react';
import { SmartToy, MoreVert } from '@mui/icons-material';
import { IconButton, Chip, Menu, MenuItem, ListItemText } from '@mui/material';
import { DynamicContainer } from './common/DynamicContainer';
import { toast } from 'react-toastify';
import AgentToolsDrawer from './AIAgentsDrawer/AgentToolsDrawer';
import AgentPromptsDrawer from './AIAgentsDrawer/AgentPromptsDrawer';
import AgentTokenUsageDrawer from './AIAgentsDrawer/AgentTokenUsageDrawer';
import AgentStaticTasksDrawer from '../agent/drawers/AgentStaticTasksDrawer';
import AgentConfigurationModal from './AIAgentsDrawer/AgentConfigurationModal';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}asrosync/agents`;
const MODEL_CFG_BASE = `${process.env.REACT_APP_BACKEND_URL}asrosync/model-configurations`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

const AIAgents = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [toolsDrawerOpen, setToolsDrawerOpen] = useState(false);
  const [staticTasksDrawerOpen, setStaticTasksDrawerOpen] = useState(false);
  const [promptsDrawerOpen, setPromptsDrawerOpen] = useState(false);
  const [tokenUsageDrawerOpen, setTokenUsageDrawerOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [tenantId, setTenantId] = useState(null);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [agentForConfig, setAgentForConfig] = useState(null);
  const [modelProfiles, setModelProfiles] = useState([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedRowForMenu, setSelectedRowForMenu] = useState(null);

  // Get tenant ID from localStorage (matching SyncFlow pattern)
  useEffect(() => {
    const storedTenantId = localStorage.getItem('tenantId');
    if (!storedTenantId) {
      toast.error('Tenant ID not found. Please log in again.');
      return;
    }
    setTenantId(storedTenantId);
  }, []);

  // Fetch agents when component mounts or tenantId changes
  useEffect(() => {
    if (tenantId) {
      fetchAgents();
    }
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${MODEL_CFG_BASE}/tenant/${tenantId}?activeOnly=false`, {
          method: 'GET',
          headers: getAuthHeaders(),
        });
        if (!r.ok || cancelled) return;
        const data = await r.json();
        if (!cancelled) setModelProfiles(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setModelProfiles([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const fetchAgents = async () => {
    if (!tenantId) {
      console.warn('Tenant ID not available, skipping fetch');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/tenant/${tenantId}`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        if (response.status === 404) {
          // Tenant not found - might be invalid tenantId
          toast.error('Tenant not found. Please check your login session.');
        } else if (response.status === 403) {
          toast.error('Access denied. You do not have permission to access this tenant.');
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        setAgents([]);
        return;
      }

      const data = await response.json();
      const mappedData = data.map(item => ({
        ...item,
        id: item.tenantAgentId,
        agentMode: item.agent_mode || 'CHAT-UI', // Map snake_case to camelCase
        modelConfigurationId:
          item.modelConfigurationId ?? item.model_configuration_id ?? null,
      }));
      setAgents(mappedData);
    } catch (error) {
      console.error('Error fetching agents:', error);
      toast.error(`Error fetching agents: ${error.message}`);
      setAgents([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async (data) => {
    if (!tenantId) {
      toast.error('Tenant ID not found. Please log in again.');
      throw new Error('Tenant ID not available');
    }

    try {
      const cleanData = Object.fromEntries(
        Object.entries(data).filter(([k, v]) => {
          if (k === 'modelConfigurationId') {
            return typeof v === 'number' && !Number.isNaN(v);
          }
          return v !== null && v !== '';
        })
      );
      
      // Map agentMode to agent_mode for backend
      if (cleanData.agentMode !== undefined) {
        cleanData.agent_mode = cleanData.agentMode;
        delete cleanData.agentMode;
      }

      const response = await fetch(`${API_BASE_URL}/tenant/${tenantId}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(cleanData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP error! status: ${response.status}`);
      }

      toast.success('Agent created successfully');
      await fetchAgents();
    } catch (error) {
      console.error('Create error:', error);
      throw error;
    }
  };

  const handleUpdate = async (id, data) => {
    if (!tenantId) {
      toast.error('Tenant ID not found. Please log in again.');
      throw new Error('Tenant ID not available');
    }

    try {
      const cleanData = Object.fromEntries(
        Object.entries(data).filter(([k, v]) => {
          if (k === 'modelConfigurationId') {
            return typeof v === 'number' && !Number.isNaN(v);
          }
          return v !== null && v !== '';
        })
      );
      
      // Map agentMode to agent_mode for backend
      if (cleanData.agentMode !== undefined) {
        cleanData.agent_mode = cleanData.agentMode;
        delete cleanData.agentMode;
      }

      const response = await fetch(`${API_BASE_URL}/tenant/${tenantId}/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(cleanData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP error! status: ${response.status}`);
      }

      toast.success('Agent updated successfully');
      await fetchAgents();
    } catch (error) {
      console.error('Update error:', error);
      throw error;
    }
  };

  const handleDelete = async (id) => {
    if (!tenantId) {
      toast.error('Tenant ID not found. Please log in again.');
      throw new Error('Tenant ID not available');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/tenant/${tenantId}/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      toast.success('Agent deleted successfully');
      await fetchAgents();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(`Error deleting agent: ${error.message}`);
      throw error;
    }
  };

  const handleOpenToolsDrawer = (agent) => {
    setSelectedAgent(agent);
    setToolsDrawerOpen(true);
  };

  const handleOpenStaticTasksDrawer = (agent) => {
    setSelectedAgent(agent);
    setStaticTasksDrawerOpen(true);
  };

  const handleCloseToolsDrawer = () => {
    setToolsDrawerOpen(false);
    setSelectedAgent(null);
  };

  const handleCloseStaticTasksDrawer = () => {
    setStaticTasksDrawerOpen(false);
    setSelectedAgent(null);
  };

  const handleOpenPromptsDrawer = (row) => {
    setSelectedAgent(row ?? null);
    setPromptsDrawerOpen(true);
  };
  const handleClosePromptsDrawer = () => {
    setPromptsDrawerOpen(false);
    setSelectedAgent(null);
  };

  const handleOpenTokenUsageDrawer = (row) => {
    setSelectedAgent(row ?? null);
    setTokenUsageDrawerOpen(true);
  };
  const handleCloseTokenUsageDrawer = () => {
    setTokenUsageDrawerOpen(false);
    setSelectedAgent(null);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedRowForMenu(null);
  };

  const handleMenuItemClick = (action) => {
    if (!selectedRowForMenu) return;

    switch (action) {
      case 'Tools':
        handleOpenToolsDrawer(selectedRowForMenu);
        break;
      case 'Tasks':
        handleOpenStaticTasksDrawer(selectedRowForMenu);
        break;
      case 'Prompts':
        handleOpenPromptsDrawer(selectedRowForMenu);
        break;
      case 'Token Usage':
        handleOpenTokenUsageDrawer(selectedRowForMenu);
        break;
      default:
        break;
    }

    handleMenuClose();
  };

  const handleOpenConfigModal = (agent = null) => {
    setAgentForConfig(agent);
    setConfigModalOpen(true);
  };
  const handleCloseConfigModal = () => {
    setConfigModalOpen(false);
    setAgentForConfig(null);
  };
  const handleConfigModalSave = async (data) => {
    const cleanData = Object.fromEntries(
      Object.entries(data).filter(([k, v]) => {
        if (k === 'modelConfigurationId') {
          return typeof v === 'number' && !Number.isNaN(v);
        }
        return v !== null && v !== '';
      })
    );
    if (agentForConfig) {
      await handleUpdate(agentForConfig.id, cleanData);
    } else {
      await handleCreate(cleanData);
    }
  };

  // Check if user is CDX user
  const isCdxUser = () => {
    try {
      const cdxUser = localStorage.getItem('cdx_user');
      return cdxUser === 'true';
    } catch (error) {
      console.error('Error reading cdx_user from localStorage:', error);
      return false;
    }
  };

  const columns = [
    { field: 'agentName', headerName: 'Agent Name', width: 200 },
    { field: 'agentAppName', headerName: 'App Name', width: 120 },
    { 
      field: 'agentMode', 
      headerName: 'Agent Mode', 
      width: 150,
      renderCell: (params) => {
        const mode = params.value || 'CHAT-UI';
        const colorMap = {
          'CHAT-UI': 'primary',
          'INTELLIGENT': 'success',
          'DIRECT': 'warning'
        };
        return <Chip label={mode} color={colorMap[mode] || 'default'} size="small" variant="outlined" />;
      }
    },
    {
      field: 'modelConfigurationId',
      headerName: 'LLM profile',
      width: 200,
      sortable: false,
      renderCell: (params) => {
        const id = params.row.modelConfigurationId ?? params.row.model_configuration_id;
        if (id == null || id === '') {
          return <span style={{ color: '#999' }}>—</span>;
        }
        const m = modelProfiles.find((x) => x.modelConfigurationId === id);
        return <span>{m ? m.displayName : `#${id}`}</span>;
      },
    },
    { field: 'isActive', headerName: 'Active', width: 100, type: 'boolean' },
    {
      field: 'moreOptions',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchorEl(e.currentTarget);
            setSelectedRowForMenu(params.row);
          }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const formFields = [
    {
      name: 'agentName',
      label: 'Agent Name',
      required: true,
      validate: (value) => {
        if (!value) return 'Agent name is required';
        if (value.length < 3) return 'Agent name must be at least 3 characters';
        return null;
      }
    },

    {
      name: 'agentAppName',
      label: 'Agent App Name',
      type: 'select',
      options: ['n8n', 'langchain'],
      required: false
    },
    {
      name: 'agentMode',
      label: 'Agent Mode',
      type: 'select',
      options: ['CHAT-UI', 'INTELLIGENT', 'DIRECT'],
      defaultValue: 'CHAT-UI',
      required: false
    },
    {
      name: 'agentToken',
      label: 'Agent Token',
      required: false
    },
    {
      name: 'description',
      label: 'Description',
      type: 'multiline',
      rows: 3,
      required: false
    },
    {
      name: 'agentConfig',
      label: 'Agent Config (JSON)',
      type: 'multiline',
      rows: 6,
      required: false,
      helperText: 'Enter JSON configuration'
    },
    {
      name: 'isActive',
      label: 'Active',
      type: 'checkbox',
      required: false
    }
  ];

  const initialFormData = {
    agentName: '',
    agentAppName: '',
    agentMode: 'CHAT-UI',
    agentToken: '',
    description: '',
    agentConfig: '',
    isActive: true
  };

  return (
    <>
      <DynamicContainer
        title="AI Agents"
        icon={SmartToy}
        columns={columns}
        formFields={formFields}
        initialFormData={initialFormData}
        idField="id"
        rows={agents}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        isLoading={isLoading}
        isCdxUser={isCdxUser()}
        functionCode="Agent Setup"
        prepareEditData={(row) => ({ ...initialFormData, ...row })}
        onAddClick={() => handleOpenConfigModal(null)}
        onEditClick={(row) => handleOpenConfigModal(row)}
      />

      {/* Agent Tools Drawer */}
      <AgentToolsDrawer
        open={toolsDrawerOpen}
        onClose={handleCloseToolsDrawer}
        agent={selectedAgent}
        tenantId={tenantId}
      />

      {/* Agent Static Tasks Drawer - pass agent so tasks are scoped by tenant agent instance (agentUniqueId) */}
      <AgentStaticTasksDrawer
        open={staticTasksDrawerOpen}
        onClose={handleCloseStaticTasksDrawer}
        agent={selectedAgent}
      />

      {/* Agent Prompts Drawer - pass agent so prompts are scoped by tenant agent instance (agentUniqueId) */}
      <AgentPromptsDrawer
        open={promptsDrawerOpen}
        onClose={handleClosePromptsDrawer}
        tenantId={tenantId}
        agent={selectedAgent}
      />

      <AgentTokenUsageDrawer
        open={tokenUsageDrawerOpen}
        onClose={handleCloseTokenUsageDrawer}
        tenantId={tenantId}
        agent={selectedAgent}
      />

      {/* Agent Add/Edit Modal - per application design standards */}
      <AgentConfigurationModal
        open={configModalOpen}
        onClose={handleCloseConfigModal}
        agent={agentForConfig}
        tenantId={tenantId}
        onSave={handleConfigModalSave}
        onSuccess={fetchAgents}
      />

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => handleMenuItemClick('Tools')}>
          <ListItemText primary="Tools" />
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('Tasks')}>
          <ListItemText primary="Tasks" />
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('Prompts')}>
          <ListItemText primary="Prompts" />
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('Token Usage')}>
          <ListItemText primary="Token Usage" />
        </MenuItem>
      </Menu>
    </>
  );
};

export default AIAgents;

